import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Inbox, Star, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const NotificationDropdown = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const [showPersonalOnly, setShowPersonalOnly] = useState(false);

    useEffect(() => {
        fetchNotifications();
        const subscriptionPromise = setupSubscription();

        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            subscriptionPromise.then(cleanup => cleanup && cleanup());
            // Reset filter when closing is handled but here we just resetting listeners
        };
    }, []);

    // Reset filter when dropdown closes
    useEffect(() => {
        if (!isOpen) setShowPersonalOnly(false);
    }, [isOpen]);

    const fetchNotifications = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user.id)
                .eq('is_read', false) // Only fetch unread for the dropdown
                .order('created_at', { ascending: false })
                .limit(5); // Limit to 5 for dropdown (Note: filtering on client side might hide all 5 if limit is strict, but for dropdown preview it is acceptable)

            if (error) throw error;

            setNotifications(data || []);

            // Get total unread count
            const { count, error: countError } = await supabase
                .from('notifications')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .eq('is_read', false);

            if (!countError) setUnreadCount(count || 0);

        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const setupSubscription = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const subscription = supabase
            .channel('notifications_channel')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    setNotifications(prev => [payload.new, ...prev].slice(0, 5));
                    setUnreadCount(prev => prev + 1);
                    // Customize toast for personalized notifications
                    if (payload.new.type === 'team_chat') {
                        toast('New Personal Notification', {
                            description: payload.new.title,
                            icon: <Star size={16} className="text-yellow-400 fill-yellow-400" />
                        });
                    } else {
                        toast.info(payload.new.title);
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    const updated = payload.new;
                    if (updated.is_read) {
                        setNotifications(prev => prev.filter(n => n.id !== updated.id));
                        setUnreadCount(prev => Math.max(0, prev - 1));
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
        };
    };

    const markAsRead = async (id) => {
        try {
            const { error } = await supabase
                .from('notifications')
                .update({ is_read: true })
                .eq('id', id);

            if (error) throw error;

            setNotifications(prev => prev.filter(n => n.id !== id));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { error } = await supabase
                .from('notifications')
                .update({ is_read: true })
                .eq('user_id', user.id)
                .eq('is_read', false);

            if (error) throw error;

            // Clear unread in dropdown
            setNotifications([]);
            setUnreadCount(0);
            toast.success('All notifications marked as read', { icon: <Check size={16} /> });
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        await markAsRead(notification.id);
        setIsOpen(false);

        if (notification.type === 'task') {
            navigate('/tasks', { state: { openProblemId: notification.reference_id } });
        } else if (notification.type === 'experiment') {
            // notification.link is like /experiment-tracking/:projectId
            // We want to pass openExperimentId = notification.reference_id
            navigate(notification.link, { state: { openExperimentId: notification.reference_id } });
        } else if (notification.type === 'team_chat') {
            // Redirect to channel page with channel ID
            navigate('/team-chat', { state: { selectedChannelId: notification.reference_id } });
        } else if (notification.link) {
            navigate(notification.link);
        }
    };

    const handleViewAll = () => {
        setIsOpen(false);
        navigate('/notifications');
    };

    const filteredNotifications = showPersonalOnly
        ? notifications.filter(n => n.type === 'team_chat')
        : notifications;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex flex-col lg:flex-row items-center justify-center gap-0.5 lg:gap-2 px-2 py-1 lg:p-2.5 rounded-xl transition-all duration-200 relative ${isOpen
                    ? 'bg-yellow-500/15 text-yellow-400 shadow-[0_0_15px_rgba(234,179,8,0.1)]'
                    : 'text-gray-400 hover:bg-white/8 hover:text-white'
                    }`}
                title="Notifications"
            >
                <div className="relative">
                    <Bell size={18} className="lg:w-5 lg:h-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 lg:top-0 lg:right-0 w-2 h-2 bg-red-500 rounded-full animate-pulse ring-1 lg:ring-2 ring-[#0a0e1a]" />
                    )}
                </div>
                <span className="text-[9px] lg:hidden font-medium">Alerts</span>
            </button>

            {isOpen && (
                <div className="fixed inset-x-4 top-12 lg:top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-80 glass-card border border-white/10 shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-3 border-b border-white/10 flex items-center justify-between bg-white/5">
                        <h3 className="font-semibold text-white text-sm">Notifications</h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={markAllAsRead}
                                className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200"
                                title="Mark all as read"
                            >
                                <CheckCheck size={14} />
                            </button>
                            <button
                                onClick={() => setShowPersonalOnly(!showPersonalOnly)}
                                className={`p-1 rounded-md transition-all duration-200 ${showPersonalOnly
                                    ? 'text-yellow-400 bg-yellow-400/10'
                                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                                    }`}
                                title={showPersonalOnly ? "Show All" : "Show Personal Only"}
                            >
                                <Star size={14} className={showPersonalOnly ? "fill-yellow-400" : ""} />
                            </button>
                            {unreadCount > 0 && (
                                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto scrollbar-thin">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500 text-xs">Loading...</div>
                        ) : filteredNotifications.length > 0 ? (
                            filteredNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className="p-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer group relative"
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-3">
                                        {notification.type === 'team_chat' ? (
                                            <Star size={16} className="mt-0.5 text-yellow-400 fill-yellow-400 shrink-0" />
                                        ) : (
                                            <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${notification.type === 'task' ? 'bg-blue-400' :
                                                notification.type === 'project' ? 'bg-indigo-400' :
                                                    notification.type === 'experiment' ? 'bg-teal-400' :
                                                        'bg-purple-400'
                                                }`} />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-white truncate pr-6">{notification.title}</p>
                                            <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notification.message}</p>
                                            <p className="text-[10px] text-gray-500 mt-1.5">
                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            markAsRead(notification.id);
                                        }}
                                        className="absolute top-3 right-3 text-gray-500 hover:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded"
                                        title="Mark as read"
                                    >
                                        <Check size={14} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center text-gray-500 flex flex-col items-center">
                                {showPersonalOnly ? (
                                    <>
                                        <Star size={32} className="mb-2 opacity-50 text-gray-600" />
                                        <p className="text-xs">No personal notifications</p>
                                    </>
                                ) : (
                                    <>
                                        <Inbox size={32} className="mb-2 opacity-50" />
                                        <p className="text-xs">No new notifications</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="p-2 bg-white/5 border-t border-white/10">
                        <button
                            onClick={handleViewAll}
                            className="w-full py-2 text-xs font-medium text-center text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                            View All Notifications
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
